import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPills,
  FaSearch,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaQuestionCircle,
  FaLightbulb
} from 'react-icons/fa';
import { api } from '../services/api';
import './MedicationChecker.css';

function MedicationChecker({ userId }) {
  const [medication, setMedication] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounced search for suggestions
  useEffect(() => {
    const trimmedMed = medication.trim();
    if (!trimmedMed || trimmedMed.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    console.log('🔍 Fetching suggestions for:', trimmedMed); // Debug log

    const timeoutId = setTimeout(async () => {
      try {
        setLoadingSuggestions(true);
        console.log('📡 Calling API for suggestions...'); // Debug log
        const data = await api.getMedicationSuggestions(trimmedMed);
        console.log('✅ Suggestions received:', data); // Debug log
        console.log('📊 Data type:', typeof data, 'Is array:', Array.isArray(data)); // Debug log
        
        // Handle different response formats
        let suggestionsArray = [];
        if (Array.isArray(data)) {
          suggestionsArray = data;
        } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
          suggestionsArray = data.data;
        } else if (data && typeof data === 'object' && data.suggestions) {
          suggestionsArray = Array.isArray(data.suggestions) ? data.suggestions : [];
        }
        
        console.log('📋 Processed suggestions:', suggestionsArray); // Debug log
        setSuggestions(suggestionsArray);
        setShowSuggestions(suggestionsArray.length > 0);
      } catch (error) {
        console.error('❌ Error fetching suggestions:', error);
        console.error('Error details:', error.message, error.response); // Debug log
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [medication]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkMedication = async (medicationName = null) => {
    const medToCheck = medicationName || medication;
    // Ensure medToCheck is a string
    const medString = typeof medToCheck === 'string' ? medToCheck : String(medToCheck || '');
    if (!medString.trim()) {
      setError('Please enter a medication name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setShowSuggestions(false);
      const data = await api.checkMedication(medString);
      setResult(data);
    } catch (error) {
      console.error('Error checking medication:', error);
      setError('Failed to check medication. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    const medicationName = suggestion?.name || suggestion;
    // Ensure we have a valid string
    if (!medicationName || typeof medicationName !== 'string') {
      console.error('Invalid suggestion:', suggestion);
      return;
    }
    setMedication(medicationName);
    setShowSuggestions(false);
    setSuggestions([]);
    // Automatically check the selected medication
    setTimeout(() => {
      checkMedication(medicationName);
    }, 100);
  };

  const getSafetyColor = (safety) => {
    switch (safety) {
      case 'SAFE':
        return '#27ae60';
      case 'CAUTION':
        return '#f39c12';
      case 'UNSAFE':
        return '#c00';
      default:
        return '#666';
    }
  };

  const getSafetyIcon = (safety) => {
    switch (safety) {
      case 'SAFE':
        return FaCheckCircle;
      case 'CAUTION':
        return FaExclamationTriangle;
      case 'UNSAFE':
        return FaTimesCircle;
      default:
        return FaQuestionCircle;
    }
  };

  return (
    <div className="medication-checker-modern">
      {/* Modern Search Section */}
      <motion.div
        className="medication-search-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="search-card-background"></div>
        <div className="search-card-content">
          <div className="search-header-modern">
            <motion.div
              className="search-icon-wrapper-modern"
              animate={{ 
                rotate: [0, -5, 5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaSearch className="search-icon-modern" />
              <div className="icon-glow-search"></div>
            </motion.div>
            <div className="search-header-text">
              <h3 className="search-title-modern">Check Medication Safety</h3>
              <p className="search-subtitle-modern">Enter the medication name to verify its safety during pregnancy</p>
            </div>
          </div>
          
          <div className="medication-input-group-modern">
            <div className="input-wrapper-autocomplete">
              <div className="input-icon-wrapper">
                <FaSearch className="input-icon-modern" />
              </div>
              <input
                ref={inputRef}
                type="text"
                placeholder="Type medication name (e.g., Tylenol, Acetaminophen...)"
                value={medication}
                onChange={(e) => {
                  setMedication(e.target.value);
                  setError(null);
                  if (result) setResult(null);
                }}
                onFocus={() => {
                  if (suggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !loading) {
                    if (showSuggestions && suggestions.length > 0) {
                      handleSuggestionSelect(suggestions[0]);
                    } else {
                      checkMedication();
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowSuggestions(false);
                  }
                }}
                className="medication-input-modern"
                disabled={loading}
              />
              
              {/* Autocomplete Suggestions Dropdown */}
              <AnimatePresence>
                {(showSuggestions || loadingSuggestions) && (
                  <motion.div
                    ref={suggestionsRef}
                    className="suggestions-dropdown-modern"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {loadingSuggestions ? (
                      <div className="suggestions-loading-modern">
                        <span className="loading-spinner-small"></span>
                        <span>Searching...</span>
                      </div>
                    ) : suggestions.length > 0 ? (
                      <>
                        {suggestions.map((suggestion, index) => {
                          const SafetyIcon = getSafetyIcon(suggestion.safety);
                          return (
                            <motion.div
                              key={index}
                              className="suggestion-item-modern"
                              onClick={() => handleSuggestionSelect(suggestion)}
                              whileHover={{ scale: 1.02, backgroundColor: 'rgba(233, 30, 99, 0.08)' }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="suggestion-icon-wrapper">
                                <SafetyIcon className="suggestion-safety-icon" />
                              </div>
                              <div className="suggestion-content-modern">
                                <span className="suggestion-name-modern">{suggestion.name}</span>
                                {suggestion.brandNames && (
                                  <span className="suggestion-brand-names-modern">{suggestion.brandNames}</span>
                                )}
                              </div>
                              <span 
                                className="suggestion-safety-badge-modern"
                                style={{ 
                                  backgroundColor: getSafetyColor(suggestion.safety) + '15', 
                                  color: getSafetyColor(suggestion.safety),
                                  borderColor: getSafetyColor(suggestion.safety) + '30'
                                }}
                              >
                                {suggestion.safety}
                              </span>
                            </motion.div>
                          );
                        })}
                      </>
                    ) : (
                      <div className="suggestions-empty-modern">
                        <span>No suggestions found</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <motion.button 
              className="check-medication-btn-modern" 
              onClick={checkMedication}
              disabled={loading || !medication.trim()}
              whileHover={{ scale: loading || !medication.trim() ? 1 : 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <FaSearch />
                  <span>Check Safety</span>
                </>
              )}
            </motion.button>
          </div>
          
          {error && (
            <motion.div
              className="medication-error-modern"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="error-icon-wrapper">
                <FaExclamationTriangle className="error-icon-modern" />
              </div>
              <span className="error-text-modern">{error}</span>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Results Section */}
      <AnimatePresence>
        {result && (
          <motion.div
            className="medication-result-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {result.found ? (
              <>
                {result.matches ? (
                  <div className="medication-matches-container">
                    <div className="matches-header-modern">
                      <div className="matches-icon-wrapper">
                        <FaPills className="matches-header-icon" />
                      </div>
                      <div className="matches-header-text">
                        <h4 className="matches-title-modern">Found {result.matches.length} potential match{result.matches.length !== 1 ? 'es' : ''}</h4>
                        <p className="matches-subtitle-modern">Please verify with your doctor before taking any medication</p>
                      </div>
                    </div>
                    <div className="matches-grid-modern">
                      {result.matches.map((match, index) => {
                        const SafetyIcon = getSafetyIcon(match.safety);
                        return (
                          <motion.div
                            key={index}
                            className="medication-card-modern"
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ delay: index * 0.08, duration: 0.3 }}
                            whileHover={{ scale: 1.02, y: -4 }}
                          >
                            <div className="medication-card-background"></div>
                            <div className="medication-card-content">
                              <div className="medication-card-header-modern">
                                <div className="medication-name-section-modern">
                                  <h5 className="medication-generic-name-modern">{match.genericName}</h5>
                                  {match.brandNames && (
                                    <p className="medication-brand-names-modern">{match.brandNames}</p>
                                  )}
                                </div>
                                <div 
                                  className="safety-badge-wrapper-modern"
                                  style={{ 
                                    backgroundColor: getSafetyColor(match.safety) + '15', 
                                    borderColor: getSafetyColor(match.safety) + '30'
                                  }}
                                >
                                  <SafetyIcon 
                                    className="safety-icon-modern"
                                    style={{ color: getSafetyColor(match.safety) }}
                                  />
                                  <span 
                                    className="safety-text-modern"
                                    style={{ color: getSafetyColor(match.safety) }}
                                  >
                                    {match.safety}
                                  </span>
                                </div>
                              </div>
                              
                              {match.notes && (
                                <div className="medication-notes-modern">
                                  <p className="notes-text-modern">{match.notes}</p>
                                </div>
                              )}
                              
                              {match.sourcedFrom && match.sourceUrl && (
                                <div className="medication-source-modern">
                                  <span className="source-label-modern">Source:</span>
                                  <a href={match.sourceUrl} target="_blank" rel="noopener noreferrer" className="source-link-modern">
                                    {match.sourcedFrom === 'ACOG' ? 'ACOG' : 
                                     match.sourcedFrom === 'CDC' ? 'CDC' : 
                                     match.sourcedFrom === 'FDA' ? 'FDA' : 
                                     match.sourcedFrom === 'MAYO_CLINIC' ? 'Mayo Clinic' : 
                                     match.sourcedFrom === 'WHO' ? 'WHO' : match.sourcedFrom}
                                  </a>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <motion.div
                    className="medication-single-result-card"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="single-result-background"></div>
                    <div className="single-result-content">
                      <div className="single-result-header-modern">
                        <div className="medication-name-section-modern">
                          <h4 className="medication-generic-name-large">{result.genericName}</h4>
                          {result.brandNames && (
                            <p className="medication-brand-names-large">{result.brandNames}</p>
                          )}
                        </div>
                        <div 
                          className="safety-badge-wrapper-modern large"
                          style={{ 
                            backgroundColor: getSafetyColor(result.safety) + '15', 
                            borderColor: getSafetyColor(result.safety) + '30'
                          }}
                        >
                          {React.createElement(getSafetyIcon(result.safety), { 
                            className: 'safety-icon-modern',
                            style: { color: getSafetyColor(result.safety) }
                          })}
                          <span 
                            className="safety-text-modern"
                            style={{ color: getSafetyColor(result.safety) }}
                          >
                            {result.safety}
                          </span>
                        </div>
                      </div>
                      
                      {result.notes && (
                        <div className="medication-notes-modern">
                          <p className="notes-text-modern">{result.notes}</p>
                        </div>
                      )}
                      
                      {result.sourcedFrom && result.sourceUrl && (
                        <div className="medication-source-modern">
                          <span className="source-label-modern">Source:</span>
                          <a href={result.sourceUrl} target="_blank" rel="noopener noreferrer" className="source-link-modern">
                            {result.sourcedFrom === 'ACOG' ? 'ACOG' : 
                             result.sourcedFrom === 'CDC' ? 'CDC' : 
                             result.sourcedFrom === 'FDA' ? 'FDA' : 
                             result.sourcedFrom === 'MAYO_CLINIC' ? 'Mayo Clinic' : 
                             result.sourcedFrom === 'WHO' ? 'WHO' : result.sourcedFrom}
                          </a>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </>
            ) : (
              <motion.div
                className="medication-not-found-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="not-found-background"></div>
                <div className="not-found-content">
                  <div className="not-found-icon-wrapper">
                    <FaQuestionCircle className="not-found-icon-modern" />
                    <div className="icon-glow-not-found"></div>
                  </div>
                  <h4 className="not-found-title-modern">Medication Not Found</h4>
                  <p className="not-found-message-modern">{result.message}</p>
                  <div className="medication-recommendation-card">
                    <div className="recommendation-icon-wrapper">
                      <FaLightbulb className="recommendation-icon-modern" />
                    </div>
                    <div className="recommendation-content-modern">
                      <strong className="recommendation-title-modern">Recommendation:</strong>
                      <p className="recommendation-text-modern">{result.recommendation}</p>
                    </div>
                  </div>
                  {result.generalAdvice && (
                    <div className="general-advice-card">
                      <h5 className="advice-title-modern">General Medication Safety Tips:</h5>
                      <ul className="advice-list-modern">
                        {result.generalAdvice.map((advice, index) => (
                          <li key={index} className="advice-item-modern">
                            <span className="advice-bullet-modern">•</span>
                            <span className="advice-text-modern">{advice}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer Card */}
      <motion.div
        className="medication-disclaimer-card"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="disclaimer-background"></div>
        <div className="disclaimer-content-modern">
          <div className="disclaimer-icon-wrapper">
            <FaExclamationTriangle className="disclaimer-icon-modern" />
          </div>
          <div className="disclaimer-text-wrapper">
            <strong className="disclaimer-title-modern">Important Disclaimer</strong>
            <p className="disclaimer-text-modern">
              This information is for educational purposes only. Always consult your healthcare provider before taking any medication during pregnancy. Medication safety can vary based on trimester, dosage, and individual circumstances.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default MedicationChecker;

